// Shown once a day, right after an employee signs in, when they have not
// checked in yet: "Check in now" or "Later". Later hides it until tomorrow on
// this browser; the Check in button in the top bar (AttendanceWidget) stays.
// Not shown to the Superadmin (owner) — attendance is for employees.

import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Clock, LogIn } from 'lucide-react';
import { getApiErrorMessage } from '@/shared/api/axios';
import { useUser } from '@/shared/hooks/useAuth';
import { useAttendanceMutations, useTodayLog } from '../hooks/useAttendance';
import { formatClock, istMinutesNow } from '../utils';

const todayIst = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
const keyFor = (userId: string) => `checkin-prompt:${userId}:${todayIst()}`;

function readDismissed(userId: string): boolean {
  try { return localStorage.getItem(keyFor(userId)) === 'later'; } catch { return false; }
}
function writeDismissed(userId: string) {
  try { localStorage.setItem(keyFor(userId), 'later'); } catch { /* private mode — the prompt simply returns next load */ }
}

export function CheckInPrompt() {
  const user = useUser();
  const userId = String(user?.id || '');
  const isEmployee = !!userId && String(user?.role || '').toLowerCase() !== 'superadmin';
  const { data: today, isLoading } = useTodayLog();
  const { checkIn } = useAttendanceMutations();
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => { if (userId) setDismissed(readDismissed(userId)); }, [userId]);

  if (!isEmployee || isLoading || dismissed || today?.log?.check_in) return null;

  const mins = istMinutesNow();
  const greeting = mins < 12 * 60 ? 'Good morning' : mins < 17 * 60 ? 'Good afternoon' : 'Good evening';
  const nowLabel = formatClock(`${Math.floor(mins / 60)}:${String(mins % 60).padStart(2, '0')}`);
  const later = () => { writeDismissed(userId); setDismissed(true); };

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-slate-900/50 p-4">
      <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-2xl" role="dialog" aria-label="Check in">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-success/10">
          <Clock className="h-6 w-6 text-success-text" />
        </div>
        <h2 className="text-lg font-bold text-slate-900">{greeting}, {user?.displayName?.split(' ')[0] || 'there'}</h2>
        <p className="mt-1 text-sm text-slate-500">You haven't checked in today. It's {nowLabel} now.</p>
        <button
          type="button"
          disabled={checkIn.isPending}
          onClick={() => checkIn.mutate(undefined, {
            onSuccess: (d) => toast.success(`Checked in at ${formatClock(d?.time)}. Have a good day!`),
            onError: (e) => toast.error(getApiErrorMessage(e, 'Could not check in.')),
          })}
          className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-success py-2.5 text-sm font-bold text-white hover:bg-success disabled:opacity-50"
        >
          <LogIn className="h-4 w-4" /> {checkIn.isPending ? 'Checking in…' : 'Check in now'}
        </button>
        <button type="button" onClick={later} className="mt-2 w-full rounded-xl py-2 text-sm font-semibold text-slate-500 hover:bg-slate-50">
          Later
        </button>
        <p className="mt-2 text-[11px] text-slate-400">You can also check in or out any time from the button at the top of the page.</p>
      </div>
    </div>
  );
}
